import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, ConfirmDialog, DataTable, StatusPill, toast, type DataTableColumn } from '../../components/ui';
import { formatDate, formatHours, formatMonth } from '../../lib/format';
import { useReverseManagementFee } from '../support/api';
import { useAgreementPrepaid, type AgreementPrepaid } from './PrepaidHoursCard';

type FeeMonth = AgreementPrepaid['feeMonths'][number];

/**
 * The client bank's MINIMUM MANAGEMENT FEES, beside the time log that decides
 * them. A fee is charged for a month in which the client logged no time on any
 * agreement (Jesse, 2026-11-04), so the place to see one is next to the time —
 * a balance that moved with no entry behind it is otherwise a mystery.
 *
 * It is not a row in the entries table: a fee has no person, task or note, and
 * it belongs to the client's bank, not to this agreement. Renders nothing when
 * the bank has never been charged. Respects the Time tab's date range.
 */
export function ManagementFeesCard({
  agreementId,
  clientId,
  from,
  to,
}: {
  agreementId: string;
  clientId: string;
  from?: string;
  to?: string;
}) {
  const { data } = useAgreementPrepaid(agreementId);
  const reverse = useReverseManagementFee();
  const qc = useQueryClient();
  const [target, setTarget] = useState<FeeMonth | null>(null);

  const fees = (data?.feeMonths ?? []).filter(
    (f) => (!from || f.chargedOn >= from) && (!to || f.chargedOn <= to),
  );
  if (!data || fees.length === 0) return null;
  const net = fees.reduce((s, f) => s + (f.reversed ? 0 : f.hours), 0);

  const columns: Array<DataTableColumn<FeeMonth>> = [
    { key: 'month', header: 'Month', render: (f) => formatMonth(`${f.period}-01`) },
    { key: 'on', header: 'Charged', render: (f) => formatDate(f.chargedOn) },
    {
      key: 'status',
      header: 'Status',
      render: (f) =>
        f.reversed ? (
          <StatusPill status="neutral" label="Reversed" dot={false} />
        ) : (
          <StatusPill status="warning" label="Charged" dot={false} />
        ),
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      render: (f) => (
        <span className={f.reversed ? 'text-grey-400 line-through' : undefined}>
          {formatHours(f.hours, { fixed: true })}
        </span>
      ),
    },
    {
      key: 'action',
      header: '',
      align: 'right',
      render: (f) =>
        f.reversed ? null : (
          <Button size="sm" variant="ghost" onClick={() => setTarget(f)}>
            Reverse
          </Button>
        ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Management fees"
        subtitle={`${formatHours(net, { unit: true })} charged to the client’s support bank${
          data.feesShared ? ' — shared with another agreement' : ''
        } · a month with no time logged for the client`}
        actions={
          <Link to={`/clients/${clientId}?tab=support`} className="text-body-sm text-primary hover:underline">
            Open the bank
          </Link>
        }
      />
      <DataTable columns={columns} data={fees} rowKey={(f) => f.period} />
      <ConfirmDialog
        open={target !== null}
        onOpenChange={(open) => !open && setTarget(null)}
        title={target ? `Reverse ${formatMonth(`${target.period}-01`)}’s fee?` : ''}
        description={
          target
            ? `${formatHours(target.hours, { unit: true })} go back into the client’s bank. The fee stays on the statement beside its reversal, and the month is not charged again.`
            : ''
        }
        confirmLabel="Reverse fee"
        onConfirm={async () => {
          if (!target) return;
          try {
            await reverse.mutateAsync({ clientId, period: target.period });
            await qc.invalidateQueries({ queryKey: ['agreements'] });
            toast.success(`${formatMonth(`${target.period}-01`)}’s fee reversed`);
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not reverse the fee');
            throw e;
          }
        }}
      />
    </Card>
  );
}
